import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { logger } from '../utils/logger';
import { baseAxiosConfig } from '../utils/apiConfig';

const agencyAxios = axios.create(baseAxiosConfig);

/**
 * Helper to get auth params and headers for agency requests.
 */
async function getAuthConfig() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Authentication required');
  }
  const token = await user.getIdToken();
  return {
    params: { user: user.email },
    headers: { Authorization: `Bearer ${token}` },
  };
}

// ---------------------------------------------------------------------------
// URL analysis
// ---------------------------------------------------------------------------

/**
 * Analyze a business URL with Gemini AI to extract a search keyword.
 * Returns { business_name, niche, location, search_keyword, vertical_name }
 * @param {string} url - The business URL to analyze
 */
export async function analyzeUrl(url) {
  const config = await getAuthConfig();
  const response = await agencyAxios.post(
    '/agency/analyze-url',
    { url },
    config,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Scrape operations
// ---------------------------------------------------------------------------

/**
 * Trigger a scrape for the given vertical.
 * @param {string} vertical - Vertical ID to scrape
 * @param {Object} [options] - Additional options
 * @param {number} [options.month] - Month number (1-12), defaults to current month
 * @param {string} [options.niche] - Business niche for competitor discovery
 * @param {string} [options.region] - Target region for competitor discovery
 */
export async function triggerScrape(vertical, { month, niche, region } = {}) {
  const config = await getAuthConfig();
  const params = { ...config.params };
  if (month != null) {
    params.month = month;
  }
  if (niche) {
    params.niche = niche;
  }
  if (region) {
    params.region = region;
  }
  const response = await agencyAxios.post(`/agency/scrape/${encodeURIComponent(vertical)}`, null, {
    params,
    headers: config.headers,
  });
  logger.info('Scrape triggered:', vertical);
  return response.data;
}

/**
 * Discover competitors for a niche in a location, or by raw keyword.
 *
 * When ``keyword`` is provided and both ``niche`` and ``location`` are empty
 * the keyword is used as a direct Google Places text query (e.g.
 * ``"dog walker memphis"``).
 *
 * @param {string} niche - Business niche / category
 * @param {string} location - Target location
 * @param {number} [targetCount=20] - Number of competitors to find
 * @param {string} [keyword=''] - Raw search keyword (used when niche/location are absent)
 */
export async function discoverCompetitors(niche, location, targetCount = 20, keyword = '') {
  const config = await getAuthConfig();
  const body = { niche, location, target_count: targetCount };
  if (keyword) {
    body.keyword = keyword;
  }
  const response = await agencyAxios.post('/search/discover-competitors', body, {
    params: config.params,
    headers: config.headers,
  });
  return response.data;
}

/**
 * Get stored competitor targets for a vertical.
 * @param {string} vertical - Vertical ID
 */
export async function getCompetitorTargets(vertical) {
  const config = await getAuthConfig();
  const response = await agencyAxios.get(`/agency/competitor-targets/${encodeURIComponent(vertical)}`, config);
  return response.data;
}

/**
 * Get scrape status for all agency verticals.
 */
export async function getScrapeStatus() {
  const config = await getAuthConfig();
  const response = await agencyAxios.get('/agency/scrape-status', config);
  logger.info('Scrape status fetched');
  return response.data;
}

/**
 * Get detailed status for a specific vertical.
 * @param {string} vertical - Vertical ID
 */
export async function getVerticalStatus(vertical) {
  const config = await getAuthConfig();
  const response = await agencyAxios.get(`/agency/vertical-status/${encodeURIComponent(vertical)}`, config);
  return response.data;
}

/**
 * Get scrape details: businesses, execution history, and assigned clients.
 * @param {string} vertical - Vertical ID
 */
export async function getScrapeDetails(vertical) {
  const config = await getAuthConfig();
  const response = await agencyAxios.get(`/agency/scrape-details/${encodeURIComponent(vertical)}`, config);
  return response.data;
}

// ---------------------------------------------------------------------------
// Client operations
// ---------------------------------------------------------------------------

/**
 * Get the agency's client list.
 */
export async function getAgencyClients() {
  const config = await getAuthConfig();
  const response = await agencyAxios.get('/agency/clients', config);
  return response.data;
}

/**
 * Add a new client to the agency.
 * @param {Object} clientData - { name, email, password, business_name, verticals, notes }
 */
export async function addAgencyClient(clientData) {
  const config = await getAuthConfig();
  const response = await agencyAxios.post('/agency/clients', clientData, config);
  logger.info('Client added:', clientData.name || clientData.email);
  return response.data;
}

/**
 * Update the full client list (for reordering, assignment changes, etc.).
 * @param {Array} clients - Full client list
 */
export async function updateAgencyClients(clients) {
  const config = await getAuthConfig();
  const response = await agencyAxios.put('/agency/clients', { clients }, config);
  logger.info('Clients updated');
  return response.data;
}

// ---------------------------------------------------------------------------
// Member operations
// ---------------------------------------------------------------------------

/**
 * Get all members of the agency.
 */
export async function getAgencyMembers() {
  const config = await getAuthConfig();
  const response = await agencyAxios.get('/agency/members', config);
  return response.data;
}

/**
 * Create a new agency member (user account + agency assignment).
 * @param {Object} memberData - { email, password, vertical, first_name, last_name, tier }
 */
export async function createAgencyMember(memberData) {
  const config = await getAuthConfig();
  const response = await agencyAxios.post('/agency/members', memberData, config);
  logger.info('Member created:', memberData.email);
  return response.data;
}

/**
 * Remove a member from the agency.
 * @param {string} email - Member email to remove
 */
export async function deleteAgencyMember(email) {
  const config = await getAuthConfig();
  const response = await agencyAxios.delete(`/agency/members/${encodeURIComponent(email)}`, config);
  logger.info('Member removed:', email);
  return response.data;
}

// ---------------------------------------------------------------------------
// Keyword scrape operations
// ---------------------------------------------------------------------------

/**
 * Trigger a keyword-focused scrape for a vertical.
 * Uses the same Gemini AI analysis + Google Places competitor discovery
 * flow as the review scrape, but namespaces the vertical as a keyword scrape.
 * @param {string} vertical - Vertical search keyword / ID
 * @param {Object} [options]
 * @param {string} [options.niche] - Business niche for competitor discovery
 * @param {string} [options.region] - Target region for competitor discovery
 */
export async function triggerKeywordScrape(vertical, { niche = '', region = '' } = {}) {
  const config = await getAuthConfig();
  const response = await agencyAxios.post(
    '/agency/keyword-scrape',
    { vertical, niche, region },
    config,
  );
  logger.info('Keyword scrape triggered:', vertical);
  return response.data;
}
